import { logger } from './logger';

interface InlineExposureTrackerState {
  observer: IntersectionObserver;
  timeoutId: ReturnType<typeof setTimeout> | null;
  viewableStart: number | null;
}

export interface InlineExposureTrackingParams {
  exposureUrl?: string;
  recommendationId?: string;
  linkElement?: HTMLElement | null;
  sessionId?: string;
  logPrefix?: string;
}

export interface InlineExposureTracker {
  startTracking: (params: InlineExposureTrackingParams) => void;
  cleanup: () => void;
}

/**
 * Creates an inline exposure tracker that fires AdMesh exposure pixels when
 * detected links meet the MRC viewability threshold (50% visible for 1 second).
 */
export const createInlineExposureTracker = (): InlineExposureTracker => {
  const firedKeys = new Set<string>();
  const activeTrackers = new Map<string, InlineExposureTrackerState>();

  const cleanupTracker = (key: string) => {
    const tracker = activeTrackers.get(key);
    if (!tracker) {
      return;
    }
    tracker.observer.disconnect();
    if (tracker.timeoutId) {
      clearTimeout(tracker.timeoutId);
    }
    activeTrackers.delete(key);
  };

  const startTracking = ({
    exposureUrl,
    recommendationId,
    linkElement,
    sessionId,
    logPrefix = '[AdMesh Exposure]'
  }: InlineExposureTrackingParams) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!exposureUrl || !linkElement) {
      if (logPrefix) {
        logger.warn(`${logPrefix} ⚠️ Missing exposure tracking data`);
      }
      return;
    }

    const dedupeKey = `${sessionId || 'anonymous'}::${recommendationId || exposureUrl}`;

    if (firedKeys.has(dedupeKey) || activeTrackers.has(dedupeKey)) {
      return;
    }

    const trackerState: InlineExposureTrackerState = {
      observer: undefined as unknown as IntersectionObserver,
      timeoutId: null,
      viewableStart: null
    };

    const fireExposurePixel = () => {
      cleanupTracker(dedupeKey);
      firedKeys.add(dedupeKey);

      fetch(exposureUrl, { method: 'GET', keepalive: true })
        .then(() => {
          if (logPrefix) {
            logger.log(`${logPrefix} ✅ Exposure pixel fired`);
          }
        })
        .catch(() => {
          if (logPrefix) {
            logger.warn(`${logPrefix} ⚠️ Failed to fire exposure pixel`);
          }
          firedKeys.delete(dedupeKey);
        });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visibility = entry.intersectionRatio;

          if (visibility >= 0.5) {
            if (trackerState.viewableStart === null) {
              trackerState.viewableStart = performance.now();
              trackerState.timeoutId = setTimeout(fireExposurePixel, 1000);
            }
          } else {
            trackerState.viewableStart = null;
            if (trackerState.timeoutId) {
              clearTimeout(trackerState.timeoutId);
              trackerState.timeoutId = null;
            }
          }
        });
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: '0px'
      }
    );

    trackerState.observer = observer;
    observer.observe(linkElement as Element);
    activeTrackers.set(dedupeKey, trackerState);
  };

  const cleanup = () => {
    Array.from(activeTrackers.keys()).forEach(cleanupTracker);
  };

  return {
    startTracking,
    cleanup
  };
};
